<template>
  <div class="paper-config">
    <div class="form-title">试卷配置</div>
    <div v-loading="loading" class="form-body">
      <el-form label-width="130px" @submit.native.prevent>
        <el-form-item label="试卷随机方式：">
          <el-radio-group v-model="form.questionConfig">
            <el-radio :label="1">千人千题试卷</el-radio>
            <el-radio :label="2">随机试题固定顺序</el-radio>
            <el-radio :label="3">随机试题随机顺序</el-radio>
            <el-radio :label="4">AB或多种试卷</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="form.questionConfig == 4">
          <el-form-item label="试卷种类：">
            <el-input-number
              v-model="form.variantCount"
              :min="2"
              :step="1"
              :precision="0"
              :controls="false"
              class="num-input"
              placeholder="请输入试卷种类数量"
            ></el-input-number>
          </el-form-item>
          <el-form-item label="种类分配方式：">
            <el-radio-group v-model="form.variantAllocation">
              <el-radio :label="1">均分</el-radio>
              <el-radio :label="2">随机数量</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>
        <el-form-item label="抽取试题数量：">
          <div class="store-info">当前试卷中包含&nbsp;&nbsp;{{ storeInfoText }}</div>
          <div v-for="row in visibleQuestionRows" :key="row.key" class="num-row">
            <label class="row-label">{{ row.label }}数（{{ row.total }}）</label>
            <el-input-number
              v-model="row.number"
              :min="0"
              :max="row.total"
              :step="1"
              :precision="0"
              :disabled="row.total == 0 || isTypeDisabled(row)"
              :controls="false"
              class="num-input"
            ></el-input-number>
            <template v-if="isPractice">
              <span class="score-label">分数</span>
              <el-input-number
                v-model="row.score"
                :min="0"
                :step="1"
                :precision="0"
                :disabled="row.number == 0"
                :controls="false"
                class="num-input"
              ></el-input-number>
            </template>
          </div>
          <div class="num-row">
            <label class="row-label">设置及格线（40-90）</label>
            <el-input-number
              v-model="form.passScore"
              :min="40"
              :max="90"
              :step="1"
              :precision="0"
              :controls="false"
              class="num-input"
            ></el-input-number>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <div class="footer">
      <el-button type="primary" :loading="saveLoading" @click="save">保存</el-button>
    </div>
  </div>
</template>
<script>
// 题型配置（key 对应接口字段前缀）
const questionTypeList = [
  { key: 'judge', label: '判断题' },
  { key: 'single', label: '单选题' },
  { key: 'multiple', label: '多选题' },
  { key: 'input', label: '填空题' },
  { key: 'calcAnswer', label: '计算题' },
  { key: 'shortAnswer', label: '简答题' },
  { key: 'discuss', label: '论述题' },
  { key: 'caseAnalysis', label: '案例分析题' },
  { key: 'comprehensive', label: '综合题' },
  { key: 'scenario', label: '情景模拟题' },
]
// 不展示的题型（填空题、情景模拟题）
const hiddenQuestionTypes = ['input', 'scenario']
// 理论试卷仅可选择单选、多选、判断
const theoryQuestionTypes = ['single', 'multiple', 'judge']
import { paperGetQuestionConfig, paperEditQuestionConfig } from '@/api/exam'
import to from 'await-to'
export default {
  name: 'ExamPaperConfig',
  data() {
    return {
      paperId: this.$route.query.id,
      paperType: 2,
      loading: false,
      saveLoading: false,
      form: {
        questionConfig: 1,
        variantCount: undefined,
        variantAllocation: undefined,
        passScore: undefined,
      },
      questionRows: questionTypeList.map(item => ({
        ...item,
        number: 0,
        total: 0,
        score: undefined,
      })),
    }
  },
  computed: {
    // 是否实操试卷（1-实操 2-理论）
    isPractice() {
      return this.paperType == 1
    },
    // 展示的题型行（隐藏填空题、情景模拟题）
    visibleQuestionRows() {
      return this.questionRows.filter(row => !hiddenQuestionTypes.includes(row.key))
    },
    // 当前试卷各题型现存数量说明
    storeInfoText() {
      return this.visibleQuestionRows.map(row => `${row.label}数:${row.total}`).join(' ')
    },
    // 试卷总分：理论=Σ题数（每题1分）；实操=Σ题数×分数
    totalScore() {
      return this.questionRows.reduce((sum, row) => {
        const number = row.number || 0
        return sum + (this.isPractice ? number * (row.score || 0) : number)
      }, 0)
    },
  },
  mounted() {
    this.getConfig()
  },
  methods: {
    // 理论试卷仅可选择单选、多选、判断，其余题型禁用
    isTypeDisabled(row) {
      return this.paperType == 2 && !theoryQuestionTypes.includes(row.key)
    },
    async getConfig() {
      this.loading = true
      const [res, err] = await to(paperGetQuestionConfig({ testPaperId: this.paperId }))
      this.loading = false
      if (err) return this.$message.warning(err.msg)
      const data = res.data || {}
      this.paperType = data.paperType || 2
      this.form.questionConfig = data.questionConfig || 1
      this.form.variantCount = data.variantCount || undefined
      this.form.variantAllocation = data.variantAllocation || undefined
      this.form.passScore = data.passScore || undefined
      this.questionRows.forEach(row => {
        row.total = data[`${row.key}NumberTotal`] || 0
        row.number = data[`${row.key}Number`] || 0
        row.score = this.isPractice ? data[`${row.key}Score`] || undefined : undefined
      })
    },
    async save() {
      const { questionConfig, variantCount, variantAllocation, passScore } = this.form
      // 1、抽题数量校验：规则中的题型数量必须小于等于现存题型数
      for (const row of this.questionRows) {
        const number = row.number || 0
        if (number > row.total) {
          return this.$message.warning(`${row.label}抽取数量不能超过现存数量${row.total}`)
        }
      }
      const totalCount = this.questionRows.reduce((sum, row) => sum + (row.number || 0), 0)
      if (totalCount == 0) {
        return this.$message.warning('请设置抽取试题数量')
      }
      // 2、AB或多种试卷：种类数量与分配方式必填
      if (questionConfig == 4) {
        if (!variantCount || variantCount < 2) {
          return this.$message.warning('请设置不小于2的试卷种类数量')
        }
        if (!variantAllocation) {
          return this.$message.warning('请选择种类分配方式')
        }
      }
      // 3、及格分数：40-90的正整数
      if (!passScore || passScore < 40 || passScore > 90) {
        return this.$message.warning('及格分数请填写40-90的正整数')
      }
      // 4、总分强校验：总分超过100分则提示
      if (this.totalScore > 100) {
        return this.$message.warning('总分超过100，请重新设置分值')
      }
      // 组装保存参数
      const params = {
        id: this.paperId - 0,
        questionConfig,
        variantCount: questionConfig == 4 ? variantCount : undefined,
        variantAllocation: questionConfig == 4 ? variantAllocation : undefined,
        passScore,
        hasAll: false,
      }
      this.questionRows.forEach(row => {
        params[`${row.key}Number`] = row.number || 0
        if (this.isPractice) {
          params[`${row.key}Score`] = row.number > 0 ? row.score || 0 : 0
        }
      })
      this.saveLoading = true
      const [, err] = await to(paperEditQuestionConfig(params))
      this.saveLoading = false
      if (err) return this.$message.warning(err.msg)
      this.$message.success('保存成功')
      this.goBack()
    },
    goBack() {
      this.$router.push('/exam/paper')
    },
  },
}
</script>
<style lang="scss" scoped>
.paper-config {
  background-color: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .form-title {
    height: 72px;
    font-size: 18px;
    font-weight: 500;
    color: #333333;
    padding: 0 20px;
    line-height: 72px;
    border-bottom: 1px solid #e8ebf2;
    margin-bottom: 30px;
    flex-shrink: 0;
  }
  .form-body {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 30px;
  }
  .footer {
    flex-shrink: 0;
    padding: 12px 20px;
    border-top: 1px solid #e8ebf2;
    text-align: center;
  }
  .num-input {
    width: 140px;
    flex-shrink: 0;
    /deep/.el-input__inner {
      border-radius: 2px;
      text-align: center;
    }
  }
  .store-info {
    max-width: 500px;
    font-size: 12px;
    color: #999999;
    line-height: 20px;
    margin-bottom: 16px;
  }
  .num-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    .row-label {
      width: 160px;
      text-align: right;
      padding-right: 12px;
      font-size: 14px;
      color: #333333;
      line-height: 20px;
    }
    .score-label {
      margin: 0 12px 0 24px;
      font-size: 14px;
      color: #333333;
    }
  }
  /deep/.el-form-item {
    margin-bottom: 30px;
  }
  /deep/.el-input__inner {
    border-radius: 2px;
  }
}
</style>
